import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import { useScenarioMode } from "../../context/ScenarioModeContext";
import { ScenarioBanner, ScenarioToggle } from "../../components/planning/ScenarioModeControls";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import CashFlowSummary from "../../components/planning/cashflow/CashFlowSummary";
import CashFlowGrid from "../../components/planning/cashflow/CashFlowGrid";
import CashFlowDrilldown from "../../components/planning/cashflow/CashFlowDrilldown";
import CashChart from "../../components/planning/cashflow/CashChart";
import { MONTHS, fmtSEK } from "../../components/planning/revenue/format";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function CashFlowPage() {
  const { isAdmin } = useAuth();
  const { scenarioMode } = useScenarioMode();
  const [companies, setCompanies] = useState([]);
  const { companyId, setCompanyId, year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drilldown, setDrilldown] = useState(null); // { line, month }

  const [openingOpen, setOpeningOpen] = useState(false);
  const [openingForm, setOpeningForm] = useState({ month: "1", amount: "" });
  const [itemOpen, setItemOpen] = useState(false);
  const [itemForm, setItemForm] = useState({ month: "1", direction: "OUTFLOW", amount: "", label: "", category: "TAX" });

  useEffect(() => {
    api.get("/companies")
      .then((c) => {
        setCompanies(c.data);
        if (c.data.length > 0 && !c.data.some((co) => co.id === companyId)) setCompanyId(c.data[0].id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    setData(null);
    api
      .get(`/companies/${companyId}/periods/years`)
      .then((res) => {
        setYears(res.data);
        const pick = pickActiveYear(res.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [companyId, year]);

  const load = async () => {
    if (!companyId || !year) return;
    setLoading(true);
    try {
      const res = await api.get(scenarioMode ? "/scenario-outcome/cash-flow" : "/cash-flow", { params: { company_id: companyId, year } });
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [companyId, year, scenarioMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveOpening = async () => {
    try {
      await api.put("/cash-flow/opening-balance", {
        company_id: companyId,
        year,
        month: Number(openingForm.month),
        amount: Number(openingForm.amount),
      });
      toast.success("Opening cash balance saved");
      setOpeningOpen(false);
      setOpeningForm({ month: "1", amount: "" });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveItem = async () => {
    try {
      await api.post("/cash-flow/manual-items", {
        company_id: companyId,
        cash_year: year,
        cash_month: Number(itemForm.month),
        direction: itemForm.direction,
        amount: Number(itemForm.amount),
        label: itemForm.label,
        category: itemForm.category,
      });
      toast.success("Manual cash item added");
      setItemOpen(false);
      setItemForm({ month: "1", direction: "OUTFLOW", amount: "", label: "", category: "TAX" });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/cash-flow/manual-items/${id}`);
      toast.success("Manual cash item removed");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openingThisYear = (data?.opening_balances || []).filter((o) => o.year === year);
  const existingOpening = openingThisYear.find((o) => o.month === Number(openingForm.month));

  const clearOpening = async () => {
    try {
      await api.put("/cash-flow/opening-balance", { company_id: companyId, year, month: Number(openingForm.month), amount: null });
      toast.success("Opening cash balance cleared");
      setOpeningOpen(false);
      setOpeningForm({ month: "1", amount: "" });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="cashflow-page">
      <PageHeader
        title="Cash Flow"
        subtitle="When money is expected to enter or leave the bank — read-only calculated output from approved source modules"
        actions={
          <div className="flex items-center gap-2">
            <ScenarioToggle />
            {isAdmin && year && (
              <>
                <Button variant="outline" onClick={() => setOpeningOpen(true)} data-testid="cf-opening-button">
                  Opening cash
                </Button>
                <Button variant="outline" onClick={() => setItemOpen(true)} data-testid="cf-add-item-button">
                  Add manual item
                </Button>
              </>
            )}
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="cf-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {years.length > 0 && (
              <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="cf-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`cf-year-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        }
      />
      <ScenarioBanner meta={data?.scenario_meta} />

      {companyId && years.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="cf-no-years">
          <EmptyState
            title="No financial years created"
            description="Create a financial year under Administration → Companies before viewing cash flow."
            testId="cf-no-years-state"
          />
        </div>
      )}

      {year && data && (
        <>
          {data.warnings?.length > 0 && (
            <div className="mb-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3 flex gap-2" data-testid="cf-warnings">
              <AlertTriangle className="h-4 w-4 text-[#B45309] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-slate-900">Missing assumptions</div>
                <ul className="text-xs text-slate-600 mt-1 space-y-0.5">
                  {data.warnings.map((w, i) => <li key={i} data-testid={`cf-warning-${i}`}>{w}</li>)}
                </ul>
              </div>
            </div>
          )}
          <CashFlowSummary summary={data.summary} />
          <CashChart months={data.months} />
          <CashFlowGrid data={data} loading={loading} onDrilldown={(line, month) => setDrilldown({ line, month })} />

          {isAdmin && (data.manual_items?.length > 0 || openingThisYear.length > 0) && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {openingThisYear.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="cf-opening-list">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Opening cash balances {year}</div>
                  {openingThisYear.map((o) => (
                    <div key={o.id} className="flex justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                      <span className="text-slate-700">{MONTHS[o.month - 1]} {o.year}</span>
                      <span className="font-semibold text-slate-900">{fmtSEK(o.amount)} kr <span className="text-[10px] text-slate-400 font-normal">INPUT</span></span>
                    </div>
                  ))}
                </div>
              )}
              {data.manual_items?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="cf-manual-items">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Manual cash items {year}</div>
                  {data.manual_items.filter((i) => i.cash_year === year).map((item) => (
                    <div key={item.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0" data-testid={`cf-manual-item-${item.id}`}>
                      <span className="text-slate-700">{item.label} <span className="text-[10px] text-slate-400">{MONTHS[item.cash_month - 1]} · {item.category}</span></span>
                      <span className="flex items-center gap-2">
                        <span className={`font-semibold ${item.direction === "OUTFLOW" ? "text-[#B91C1C]" : "text-[#047857]"}`}>
                          {item.direction === "OUTFLOW" ? "−" : "+"}{fmtSEK(item.amount)} kr
                        </span>
                        <button className="text-slate-300 hover:text-[#B91C1C]" onClick={() => deleteItem(item.id)} data-testid={`cf-delete-item-${item.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <CashFlowDrilldown
        open={!!drilldown}
        onClose={() => setDrilldown(null)}
        line={drilldown?.line}
        month={drilldown?.month || 1}
        data={data}
        year={year}
      />

      <Dialog open={openingOpen} onOpenChange={setOpeningOpen}>
        <DialogContent data-testid="cf-opening-dialog">
          <DialogHeader>
            <DialogTitle>Opening cash balance — {year}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-slate-500">
              Finance-entered opening balance (CASH-001). Later months roll from the previous month's closing cash; an explicit balance supersedes the roll.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Month</Label>
                <Select value={openingForm.month} onValueChange={(v) => setOpeningForm({ ...openingForm, month: v })}>
                  <SelectTrigger data-testid="cf-opening-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((l, i) => <SelectItem key={i + 1} value={String(i + 1)}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Amount (SEK)</Label>
                <Input type="number" value={openingForm.amount} onChange={(e) => setOpeningForm({ ...openingForm, amount: e.target.value })} data-testid="cf-opening-amount" />
              </div>
            </div>
          </div>
          <DialogFooter>
            {existingOpening && (
              <Button variant="outline" className="text-[#B91C1C] border-[#FECACA] hover:bg-[#FEF2F2] mr-auto" onClick={clearOpening} data-testid="cf-opening-clear">
                Clear {MONTHS[existingOpening.month - 1]} balance
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpeningOpen(false)} data-testid="cf-opening-cancel">Cancel</Button>
            <Button onClick={saveOpening} disabled={openingForm.amount === "" || Number.isNaN(Number(openingForm.amount))} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="cf-opening-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent data-testid="cf-item-dialog">
          <DialogHeader>
            <DialogTitle>Manual cash item — {year}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-slate-500">
              Explicit Finance planning input (e.g. preliminary corporate tax payments). Category TAX feeds the Corporate Tax Payments line.
            </p>
            <div className="space-y-1.5">
              <Label>Label</Label>
              <Input value={itemForm.label} onChange={(e) => setItemForm({ ...itemForm, label: e.target.value })} placeholder="Preliminary corporate tax" data-testid="cf-item-label" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Month</Label>
                <Select value={itemForm.month} onValueChange={(v) => setItemForm({ ...itemForm, month: v })}>
                  <SelectTrigger data-testid="cf-item-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((l, i) => <SelectItem key={i + 1} value={String(i + 1)}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Amount (SEK)</Label>
                <Input type="number" value={itemForm.amount} onChange={(e) => setItemForm({ ...itemForm, amount: e.target.value })} data-testid="cf-item-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Direction</Label>
                <Select value={itemForm.direction} onValueChange={(v) => setItemForm({ ...itemForm, direction: v })}>
                  <SelectTrigger data-testid="cf-item-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OUTFLOW">Outflow (payment)</SelectItem>
                    <SelectItem value="INFLOW">Inflow (receipt)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={itemForm.category} onValueChange={(v) => setItemForm({ ...itemForm, category: v })}>
                  <SelectTrigger data-testid="cf-item-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TAX">Corporate tax</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemOpen(false)} data-testid="cf-item-cancel">Cancel</Button>
            <Button
              onClick={saveItem}
              disabled={!itemForm.label.trim() || itemForm.amount === "" || Number(itemForm.amount) <= 0 || Number.isNaN(Number(itemForm.amount))}
              className="bg-[#0052CC] hover:bg-[#0047B3]"
              data-testid="cf-item-save"
            >
              Add item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
